// AF-3.16 — EvidenceDrawer: reusable provenance/evidence panel.
//
// Any intelligence object (opportunity, recommendation, action plan,
// measurement, outcome, learning, environment rule, control decision)
// can open this drawer. It renders ONLY what the backend's own
// `evidence`/`provenance` fields actually contain -- no fabricated
// descriptions, no invented source objects. Missing evidence is shown
// as an explicit empty state, never silently hidden.

import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { ConfidenceIndicator } from "./Badges";
import { EmptyState } from "./StateViews";

function ProvenanceIdGroup({ label, ids = [] }) {
  if (!ids || ids.length === 0) return null;
  return (
    <div className="mb-3" data-testid={`provenance-group-${label}`}>
      <p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)] mb-1">{label.replace(/_/g, " ")}</p>
      <div className="flex flex-wrap gap-1">
        {ids.map((id) => (
          <span key={id} className="text-[9px] font-mono text-[var(--text-secondary)] bg-[var(--surface-sunken)] border border-[var(--border)] rounded px-1.5 py-0.5" data-testid="provenance-id-chip">
            {id.slice(0, 8)}…
          </span>
        ))}
      </div>
    </div>
  );
}

export function EvidenceDrawer({ open, onOpenChange, title = "Evidence", evidence = [], provenance = {}, unknowns = [], causality }) {
  const provenanceEntries = Object.entries(provenance || {}).filter(([, v]) => Array.isArray(v) && v.length > 0);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto bg-[var(--surface)] border-[var(--border)]" data-testid="evidence-drawer">
        <SheetHeader>
          <SheetTitle className="text-[var(--text-primary)] text-[14px]">{title}</SheetTitle>
          <SheetDescription className="text-[var(--text-tertiary)] text-[11px]">
            Why Martian believes this -- evidence and provenance only, nothing fabricated.
          </SheetDescription>
        </SheetHeader>

        {causality && (
          <div className="mt-3 mb-4 px-3 py-2 rounded bg-[var(--surface-sunken)] border border-[var(--border)]">
            <p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Causality</p>
            <p className="text-[11px] text-[var(--text-secondary)]" data-testid="evidence-causality">{causality}</p>
          </div>
        )}

        <Separator className="my-3 bg-[var(--border)]" />

        <div>
          <p className="text-[11px] font-mono uppercase text-[var(--text-tertiary)] mb-2">Evidence</p>
          {evidence.length === 0 ? (
            <EmptyState testId="evidence-empty" title="No evidence recorded" description="Martian has not attached any evidence to this object yet." />
          ) : (
            <div className="space-y-2">
              {evidence.map((e, i) => (
                <div key={i} className="rounded border border-[var(--border)] p-2.5 bg-[var(--surface-sunken)]" data-testid="evidence-item">
                  {e.source_type && <p className="text-[9px] font-mono uppercase text-[var(--text-tertiary)]">{e.source_type}{e.source_id ? ` · ${e.source_id.slice(0, 8)}…` : ""}</p>}
                  {e.statement && <p className="text-[11px] text-[var(--text-primary)] mt-0.5">{e.statement}</p>}
                  {e.confidence !== null && e.confidence !== undefined && (
                    <div className="mt-1"><ConfidenceIndicator value={e.confidence} /></div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {unknowns.length > 0 && (
          <>
            <Separator className="my-3 bg-[var(--border)]" />
            <div>
              <p className="text-[11px] font-mono uppercase text-[var(--text-tertiary)] mb-2">What remains unknown</p>
              <ul className="space-y-1">
                {unknowns.map((u, i) => (
                  <li key={i} className="text-[11px] text-[var(--text-secondary)]" data-testid="evidence-unknown-item">• {typeof u === "string" ? u : u.observation}</li>
                ))}
              </ul>
            </div>
          </>
        )}

        {provenanceEntries.length > 0 && (
          <>
            <Separator className="my-3 bg-[var(--border)]" />
            <div>
              <p className="text-[11px] font-mono uppercase text-[var(--text-tertiary)] mb-2">Provenance</p>
              {provenanceEntries.map(([label, ids]) => <ProvenanceIdGroup key={label} label={label} ids={ids} />)}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
